import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Users, Calendar, Loader2, ArrowRight, Timer, CheckCircle2, PauseCircle, Clock,
} from "lucide-react";

const CATEGORY_COLORS = {
  coding: "bg-emerald-500", deployment: "bg-sky-500", communication: "bg-fuchsia-500",
  documentation: "bg-amber-500", design: "bg-pink-500", browsing: "bg-slate-500", idle: "bg-zinc-500",
};

function fmtMinutes(m) {
  m = Math.max(0, Math.round(m || 0));
  const h = Math.floor(m / 60), r = m % 60;
  if (h === 0) return `${r}m`;
  if (r === 0) return `${h}h`;
  return `${h}h ${r}m`;
}

function isoWeek(d = new Date()) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

function shiftWeek(w, delta) {
  const [y, wk] = w.split("-W").map(Number);
  const jan4 = new Date(Date.UTC(y, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const monday = new Date(Date.UTC(y, 0, 4 - (jan4Day - 1) + (wk - 1) * 7));
  monday.setUTCDate(monday.getUTCDate() + delta * 7);
  return isoWeek(monday);
}

export default function WorkIntelligenceDash() {
  const [status, setStatus] = useState(null);
  const [week, setWeek] = useState(isoWeek(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drill, setDrill] = useState(null); // {row, date, session}
  const [padding, setPadding] = useState({ minutes: 0, note: "" });
  const [padSaving, setPadSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/work-intelligence/status");
        setStatus(r.data);
      } catch { setStatus({ enabled: false }); }
    })();
  }, []);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get("/work-intelligence/team", { params: { week } });
      setData(r.data);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load team data");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (status?.enabled) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [week, status?.enabled]);

  const openDrill = async (row, day) => {
    if (!day.submitted) {
      setDrill({ row, date: day.date, session: { date: day.date, submitted: false, hint: "Not yet submitted" } });
      return;
    }
    try {
      const r = await api.get(`/work-intelligence/employee/${row.user_id}`, { params: { date: day.date } });
      setDrill({ row, date: day.date, session: r.data });
      setPadding({ minutes: r.data?.manager_padding_minutes || 0, note: r.data?.manager_padding_note || "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const savePadding = async () => {
    if (!drill) return;
    setPadSaving(true);
    try {
      await api.patch("/work-intelligence/padding", {
        user_id: drill.row.user_id, date: drill.date,
        minutes: parseInt(padding.minutes) || 0, note: padding.note,
      });
      toast.success("Padding saved");
      await load();
      const r = await api.get(`/work-intelligence/employee/${drill.row.user_id}`, { params: { date: drill.date } });
      setDrill((d) => ({ ...d, session: r.data }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setPadSaving(false); }
  };

  const maxWeekMinutes = useMemo(() => {
    if (!data?.rows) return 1;
    return Math.max(1, ...data.rows.map((r) => r.week_total_minutes));
  }, [data]);

  if (!status) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (!status.enabled) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="wid-disabled">
        <div className="border border-border/60 rounded-lg p-8 bg-secondary/20">
          <Timer className="w-8 h-8 text-muted-foreground mb-3" />
          <h1 className="text-2xl font-display">Work Intelligence is not enabled</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Enable it under Settings → Work Intelligence to start collecting team data.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="wid-page">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-sky-500">Work Intelligence</p>
          <h1 className="font-display text-3xl mt-1">Team weekly summary</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Employees see their day before you do. Click a submitted cell to drill in and add padding.
          </p>
        </div>
        <div className="flex items-center gap-2 border border-border/60 rounded-lg px-3 py-2 bg-secondary/40">
          <button data-testid="wid-prev-week" className="p-1 text-muted-foreground hover:text-foreground"
                  onClick={() => setWeek((w) => shiftWeek(w, -1))}>
            <ArrowRight className="w-4 h-4 rotate-180" />
          </button>
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span data-testid="wid-week-label" className="text-sm font-mono">{week}</span>
          <button data-testid="wid-next-week" className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
                  disabled={week >= isoWeek(new Date())}
                  onClick={() => setWeek((w) => shiftWeek(w, 1))}>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </header>

      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-red-500" data-testid="wid-error">{error}</p>}

      {data && (
        <div className="border border-border/60 rounded-lg overflow-hidden">
          <div className="grid grid-cols-[minmax(180px,1fr)_repeat(7,minmax(0,1fr))_140px] text-xs bg-secondary/30 border-b border-border/60">
            <div className="p-3 text-muted-foreground uppercase tracking-[0.18em]">Member</div>
            {data.rows[0]?.days.map((d) => (
              <div key={d.date} className="p-3 text-muted-foreground uppercase tracking-[0.18em]">
                {new Date(d.date + "T00:00:00Z").toLocaleDateString(undefined, {
                  weekday: "short", day: "numeric", timeZone: "UTC",
                })}
              </div>
            ))}
            <div className="p-3 text-muted-foreground uppercase tracking-[0.18em]">Week total</div>
          </div>
          {data.rows.length === 0 && (
            <div className="p-6 text-center text-muted-foreground">No team members yet.</div>
          )}
          {data.rows.map((row) => {
            const barPct = (row.week_total_minutes / maxWeekMinutes) * 100;
            return (
              <div key={row.user_id} className="grid grid-cols-[minmax(180px,1fr)_repeat(7,minmax(0,1fr))_140px] border-b border-border/40 hover:bg-secondary/10"
                   data-testid={`wid-row-${row.user_id}`}>
                <div className="p-3">
                  <p className="text-sm font-medium truncate">{row.name || row.email}</p>
                  <p className="text-xs text-muted-foreground truncate">{row.role} · {row.submitted_days}/7 days submitted</p>
                </div>
                {row.days.map((d) => (
                  <button
                    key={d.date}
                    onClick={() => openDrill(row, d)}
                    data-testid={`wid-cell-${row.user_id}-${d.date}`}
                    className={`text-left p-3 border-l border-border/40 hover:bg-secondary/30 transition-colors ${d.submitted ? "" : "text-muted-foreground/50"}`}
                    title={d.submitted ? "" : "Not yet submitted"}
                  >
                    {d.submitted ? (
                      <>
                        <p className="text-xs font-mono tabular-nums">{fmtMinutes(d.total_minutes)}</p>
                        <div className="mt-1 flex h-1 rounded-sm overflow-hidden bg-secondary/40">
                          {Object.entries(d.categories || {}).map(([k, v]) => {
                            const pct = (v / (d.total_minutes || 1)) * 100;
                            return <span key={k} className={CATEGORY_COLORS[k] || "bg-slate-500"} style={{ width: `${pct}%` }} />;
                          })}
                        </div>
                        {d.padding > 0 && (
                          <p className="text-[10px] text-amber-500 mt-1">+{d.padding}m padding</p>
                        )}
                      </>
                    ) : (
                      <span className="text-xs italic">—</span>
                    )}
                  </button>
                ))}
                <div className="p-3 border-l border-border/40">
                  <p className="text-xs font-mono tabular-nums">{fmtMinutes(row.week_total_minutes)}</p>
                  <div className="mt-1 h-1 rounded-sm bg-secondary/40 overflow-hidden">
                    <div className="h-full bg-sky-500" style={{ width: `${barPct}%` }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-2xl">
          {drill && (
            <>
              <DialogHeader>
                <DialogTitle data-testid="wid-drill-title">
                  {drill.row.name || drill.row.email} · {drill.date}
                </DialogTitle>
                <DialogDescription>
                  {drill.session.submitted ? "Submitted work summary. Add padding for legitimate un-captured time." : "This day has not been submitted yet."}
                </DialogDescription>
              </DialogHeader>
              {!drill.session.submitted && (
                <div className="text-sm text-muted-foreground py-6" data-testid="wid-drill-notsubmitted">
                  <PauseCircle className="w-5 h-5 inline mr-2" />
                  You'll see this day once {drill.row.name || "the employee"} submits it. This preserves their right to add context first.
                </div>
              )}
              {drill.session.submitted && (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Categories</p>
                    {Object.entries(drill.session.categories || {}).sort(([, a], [, b]) => b - a).map(([cat, mins]) => (
                      <div key={cat} className="flex justify-between text-sm py-1">
                        <span className="capitalize">{cat}</span>
                        <span className="font-mono text-muted-foreground">{fmtMinutes(mins)}</span>
                      </div>
                    ))}
                  </div>
                  {drill.session.self_report && (
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Self report</p>
                      <p className="text-sm whitespace-pre-wrap bg-secondary/30 rounded p-3" data-testid="wid-drill-selfreport">
                        {drill.session.self_report}
                      </p>
                    </div>
                  )}
                  <div className="border-t border-border/40 pt-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Manager padding</p>
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <label className="text-xs text-muted-foreground">Minutes to add (for thinking / offline / external meetings)</label>
                        <Input
                          type="number"
                          min={0}
                          max={480}
                          data-testid="wid-padding-minutes"
                          value={padding.minutes}
                          onChange={(e) => setPadding((p) => ({ ...p, minutes: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div className="flex-[2]">
                        <label className="text-xs text-muted-foreground">Note (optional, no sensitive info)</label>
                        <Input
                          data-testid="wid-padding-note"
                          value={padding.note}
                          onChange={(e) => setPadding((p) => ({ ...p, note: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <Button data-testid="wid-save-padding" onClick={savePadding} disabled={padSaving}>
                        {padSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Save
                      </Button>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Padding is recorded in the audit log and visible to the employee.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
